import {
  Badge,
  Box,
  Card,
  Container,
  Group,
  Table,
  ThemeIcon,
} from "@mantine/core";
import { showNotification } from "@mantine/notifications";
import { IconTrophy } from "@tabler/icons-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import subSeconds from "date-fns/subSeconds";
import { GetStaticProps } from "next";
import { useTranslation } from "next-i18next";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import { useRouter } from "next/router";
import { ParsedUrlQuery } from "querystring";
import { useEffect, useRef, useState } from "react";
import { CircleFlag } from "react-circle-flags";
import {
  ListMembersResult,
  PAGE_SIZE,
  getBoard,
  joinBoard,
  listMembers,
} from "../../../../common/api";
import { authRedirect } from "../../../../common/auth/useRequireAuth";
import {
  useBoard,
  useIsBoardAdmin,
} from "../../../../common/boards/board.hooks";
import { Board } from "../../../../common/boards/board.schema";
import { Member } from "../../../../common/members/member.schema";
import { Record } from "../../../../common/record.schema";
import { useAppStore } from "../../../../common/stores/app.store";
import { useAuthStore } from "../../../../common/stores/auth.store";
import { PlanType } from "../../../../common/users/user.schema";
import { getBoardPlan, getShowCountries } from "../../../../common/util";
import EmptyBoard from "../../../../components/boards/EmptyBoard";
import BoardModalProvider from "../../../../components/common/BoardModalProvider";
import InfiniteList, {
  RenderFunction,
} from "../../../../components/common/InfiniteList";
import Loading from "../../../../components/common/Loading";
import PageTitle from "../../../../components/common/PageTitle";
import PlanPromptBar from "../../../../components/common/PlanPromptBar";
import RecordAvatar from "../../../../components/common/RecordAvatar";
import FloatAddMatch from "../../../../components/matches/FloatAddMatch";
import MemberDisplay from "../../../../components/members/MemberDisplay";
import classes from "./index.module.css";
// import { useQueryClient } from "@tanstack/react-query";
type LimitedList = ListMembersResult & { limited?: boolean };
interface Props {
  board?: Board;
  memberResult?: LimitedList;
}
const getTrophyBadge = (color: string, count?: number) => {
  const icon = (
    <ThemeIcon mt={3} color={color} variant="light" size={12}>
      <IconTrophy size={13} />
    </ThemeIcon>
  );
  if (!count) return null;
  return (
    <Badge
      variant="light"
      size="xs"
      color={color}
      leftSection={count > 1 && icon}
    >
      {count > 1 ? count : icon}
    </Badge>
  );
};
const getLeaders = async ({
  board,
  search,
  offset = 0,
}: {
  board: Board;
  search?: string;
  offset?: number;
}) => {
  if (!board) return;
  const limit = getBoardPlan(board).leaderboardSize;
  const lastPlayed = subSeconds(new Date(), board.since);
  let res: LimitedList = await listMembers({
    offset,
    search,
    params: {
      board: board._id,
      lastPlayed: { $gte: lastPlayed.toISOString() },
    },
    sort: "-score",
  });
  if (res.records.length < PAGE_SIZE && res.offset === 0) {
    res = await listMembers({
      offset,
      search,
      params: {
        board: board._id,
      },
      sort: "-lastPlayed",
      limit: PAGE_SIZE,
    });
    res.count = res.records.length;
    res.records = res.records.sort((a, b) => b.score - a.score);
  }
  // console.log("limit :", limit);
  if (limit > 0 && res.count > limit) {
    // console.log("count is over limit", res.records.length + offset, limit);
    res.count = limit;
  }
  if (limit > 0 && res.records.length + offset > limit) {
    // console.log("records over limit", res.records.length + offset, limit);
    res.records.splice(limit - offset);
    res.limited = true;
    // res;
  }
  const activeOnly = res.records.filter((r) => {
    // console.log("r :", r.name, r.matches, r.createdAt);
    //create din last 48 hrs
    const isNew = new Date(r.createdAt) > subSeconds(new Date(), 48 * 60 * 60);
    // console.log("isNew :", isNew);
    return isNew || r.matches > 0;
  });
  if (activeOnly.length > 10 && !res.limited) res.records = activeOnly;
  return res;
};
const Leaderboard = ({ board: initBoard, memberResult }: Props) => {
  // console.log("initBoard :", initBoard);
  const { t } = useTranslation();

  const [hasToken, setHasToken] = useState(false);
  const token = useAuthStore((state) => state.token);
  const user = useAuthStore((state) => state.user);
  const queryClient = useQueryClient();
  const isAdmin = useIsBoardAdmin();
  const isJoining = useRef(false);
  const setDefaultSport = useAppStore((s) => s.setDefaultSport);
  const [limited, setLimited] = useState(memberResult?.limited);
  useEffect(() => {
    setHasToken(token !== null);
  }, [token]);
  const { isError, board } = useBoard(initBoard);
  const router = useRouter();
  useEffect(() => {
    if (board?.sport) {
      setDefaultSport(board.sport);
    }
  }, [setDefaultSport, board]);
  const { mutate: join, isLoading: joining } = useMutation(joinBoard, {
    onSuccess: () => {
      queryClient.invalidateQueries(["members", board?._id]);
      router.replace(router.asPath.split("?")[0]);
      showNotification({
        message: "Successfully joined this board",
        autoClose: true,
      });
    },
    onError: () => {
      queryClient.invalidateQueries(["members", board?._id]);
      router.replace(router.asPath.split("?")[0]);
    },
  });
  useEffect(() => {
    const code = router.query.join_code;
    if (code && code.length && !joining && !isJoining.current) {
      // console.log("doing checkin");
      isJoining.current = true;
      if (token) {
        const checkAndJoin = async () => {
          const res = await listMembers({
            params: { limit: 1, board: board?._id, user: user?._id },
          });
          if (res.count >= 1) {
            showNotification({
              message: "You are already a member of this board",
              autoClose: true,
              color: "red",
            });
          } else {
            join(code.toString());
          }
        };
        checkAndJoin();
      } else {
        authRedirect(router);
      }
    }
  }, [board?._id, join, joining, router, token, user?._id]);
  if (router.isFallback) {
    return <Loading />;
  }
  if (!board) {
    return <Loading />;
  }

  if (isError) {
    return null;
  }
  const showFlags = memberResult && getShowCountries(memberResult?.records);
  // console.log("showFlags :", showFlags);
  const showTrophies =
    memberResult && !!memberResult.records.find((m) => m.tournaments > 0);
  // if (data) board = data;
  const renderFunction = (member: Member, index: number) => {
    if (!board) return <Loading />;
    return (
      <Table.Tr key={member._id}>
        <Table.Td className={classes.index}>{index + 1}</Table.Td>
        <Table.Td>
          <Group>
            <RecordAvatar className={classes.profileAvatar} record={member} />
            <MemberDisplay
              className={classes.profileName}
              member={member}
              textSize={{ base: "xs", sm: "sm", md: "md" }}
              link
            />
          </Group>
        </Table.Td>
        <Table.Td className={classes.score}>{member.score}</Table.Td>
        {showFlags && (
          <Table.Td width={20}>
            {member.country && (
              <CircleFlag
                countryCode={member.country.toLowerCase()}
                height={20}
              />
            )}
          </Table.Td>
        )}
        {showTrophies && (
          <Table.Td width={200} visibleFrom="sm">
            <Group>
              {getTrophyBadge("yellow", member.firstPlaces)}
              {getTrophyBadge("blue", member.secondPlaces)}
              {getTrophyBadge("orange", member.thirdPlaces)}
            </Group>
          </Table.Td>
        )}
      </Table.Tr>
    );
  };
  const doListMembers = async (offset: number, search?: string) => {
    const res = await getLeaders({ board, search, offset });
    setLimited(res?.limited === true);
    return res;
  };
  const getListComponent = (
    records: Record[],
    isLoading: boolean,
    renderFunction: RenderFunction
  ) => {
    return (
      <Card>
        <Table withRowBorders>
          <Table.Thead>
            <Table.Tr>
              <Table.Th></Table.Th>
              <Table.Th pb={20} className={classes.colTitle}>
                {t("name")}
              </Table.Th>
              <Table.Th pb={20} className={classes.colTitle}>
                {t("score")}
              </Table.Th>
              {showFlags && <Table.Th></Table.Th>}
              {showTrophies && <Table.Th visibleFrom="sm"></Table.Th>}
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>{records && records.map(renderFunction)}</Table.Tbody>
        </Table>
        {records.length === 0 && isLoading && <Loading />}
      </Card>
    );
  };
  const title = `${t("scoreboarder")} | ${board.name} | Leaderboard`;
  const description = t("boardDescription", {
    boardName: board.name,
  });
  const hasMembers = !!board.memberCount && board.memberCount > 0;
  return (
    <BoardModalProvider>
      <Container pt={10} px={{ base: 0, xs: 0, sm: 0, md: "sm", lg: "md" }}>
        <PageTitle
          title={title}
          image={board.image}
          description={description}
        />
        {!hasMembers && <EmptyBoard />}
        {hasMembers && (
          <Box mb={100}>
            <InfiniteList
              queryParams={[board._id]}
              queryKey={["leaderboard", board._id]}
              initialData={memberResult}
              listFunction={doListMembers}
              renderFunction={renderFunction as RenderFunction}
              getListComponent={getListComponent}
              emptyComponent={<EmptyBoard />}
            />
            {limited && isAdmin && board.plan === PlanType.FREE && (
              <Box mt={10} mb={110}>
                <PlanPromptBar />
              </Box>
            )}
            {hasToken && (
              <>
                <FloatAddMatch />
              </>
            )}
          </Box>
        )}
      </Container>
    </BoardModalProvider>
  );
};
export default Leaderboard;

interface IParams extends ParsedUrlQuery {
  boardShortLink: string;
  locale: string;
}
export const getStaticProps: GetStaticProps = async (context) => {
  const { locale } = context as { locale: string };
  const { boardShortLink } = context.params as IParams;
  // console.log("boardShortLink :", boardShortLink);
  let board: Board | undefined;
  // matchesRes: ListMatchesResult,
  const props: any = {
    ...(await serverSideTranslations(locale, ["common"])),
  };
  try {
    board = await getBoard(boardShortLink);
  } catch (e) {}
  if (!board) {
    return {
      notFound: true,
      revalidate: 60,
    };
  }
  // console.log("board :", board);
  if (board) {
    props.board = board;
    // const membersRes = await listMembers(0, "", { board: board._id });
    // props.membersRes = membersRes;
  }

  const res = await getLeaders({ board });
  props.memberResult = res;
  return {
    revalidate: 60,
    props,
  };
};
export async function getStaticPaths() {
  return { paths: [], fallback: true };
}
